import React, { useState, useMemo } from 'react';
import BasicInfoForm from './BasicInfoForm';
import NarrativeBuilder from './NarrativeBuilder';
import FollowUpQuestions from './FollowUpQuestions';
import TimelineView from './TimelineView';
import OutputsPanel from './OutputsPanel';
import RiskFlagsPanel from './RiskFlagsPanel';
import MissingInfoChecklist from './MissingInfoChecklist';
import WitnessCapture from './WitnessCapture';
import EvidenceUpload from './EvidenceUpload';
import { analyzeIncident, saveCaseDraft, updateCase } from '../../services/factFinderApi';
import analyticsTracker from '../../utils/analyticsTracker';

const INITIAL_CASE = {
  mode: 'full_record',
  incident_type: 'other',
  title: '',
  date: '',
  time: '',
  location: '',
  department: '',
  crew: '',
  assignment: '',
  equipment: '',
  supervisor: '',
  narrative: '',
  follow_up_answers: {},
  witnesses: [],
  evidence: [],
};

function FullRecordPage({ user, onBack, loadedCase, onOpenInvestigation }) {
  const [caseData, setCaseData] = useState(() => {
    if (loadedCase?.case_payload) {
      return { ...INITIAL_CASE, ...loadedCase.case_payload, mode: 'full_record' };
    }
    return INITIAL_CASE;
  });
  const [analysis, setAnalysis] = useState(() => loadedCase?.analysis_payload || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(loadedCase?._id || null);
  const [activeTab, setActiveTab] = useState(loadedCase?.analysis_payload ? 'results' : 'input');

  React.useEffect(() => {
    analyticsTracker.trackFactFinderStarted('full_record');
  }, []);

  const missingItems = useMemo(() => {
    const items = [];
    if (!caseData.title) items.push('Case title');
    if (!caseData.date) items.push('Date of incident');
    if (!caseData.time) items.push('Time of incident');
    if (!caseData.location) items.push('Location');
    if (!caseData.department) items.push('Department');
    if (!caseData.supervisor) items.push('Supervisor / manager involved');
    if (!caseData.narrative || caseData.narrative.length < 20) {
      items.push('Detailed narrative (at least 20 characters)');
    }
    if (caseData.witnesses.length === 0) items.push('Witness information');
    return items;
  }, [caseData]);

  async function handleAnalyze() {
    if (!caseData.narrative || caseData.narrative.length < 20) {
      setError('Please provide a detailed narrative (at least 20 characters).');
      return;
    }
    setLoading(true);
    setError('');
    analyticsTracker.trackFeatureUse('fact_finder', 'analyze_full_record');
    try {
      const result = await analyzeIncident(caseData);
      if (result.success) {
        setAnalysis(result.analysis);
        setActiveTab('results');
        analyticsTracker.trackFactFinderCompleted('full_record');
      } else {
        setError(result.error || 'Unable to generate record right now.');
      }
    } catch (err) {
      setError(err.message || 'Unable to generate record right now.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveDraft() {
    setLoading(true);
    setError('');
    analyticsTracker.trackCaseCreated('fact_finder', 'full_record');
    try {
      if (savedId) {
        const result = await updateCase(savedId, { caseData, analysis });
        if (result.success) {
          setError('');
          setSavedId(result.case_id);
        } else {
          setError(result.error || 'Unable to update draft.');
        }
      } else {
        const result = await saveCaseDraft({ caseData, analysis });
        if (result.success) {
          setSavedId(result.case_id);
        } else {
          setError(result.error || 'Unable to save draft.');
        }
      }
    } catch (err) {
      setError(err.message || 'Unable to save draft.');
    } finally {
      setLoading(false);
    }
  }

  function handleFollowUpChange(answers) {
    setCaseData(prev => ({ ...prev, follow_up_answers: answers }));
  }

  function setWitnesses(witnesses) {
    setCaseData(prev => ({ ...prev, witnesses }));
  }

  function setEvidence(evidence) {
    setCaseData(prev => ({ ...prev, evidence }));
  }

  function handleOpenInvestigation() {
    onOpenInvestigation({ case_payload: caseData, analysis_payload: analysis });
  }

  return (
    <div className="fact-finder-page full-record-page">
      <div className="ff-container">
        {/* Header */}
        <div className="ff-im-header">
          <div className="ff-im-header-top">
            <button onClick={onBack} className="ff-back-btn" data-testid="back-btn">
              &larr; Back
            </button>
            <div className="ff-im-actions">
              <button
                onClick={handleSaveDraft}
                className="ff-btn-secondary"
                disabled={loading}
                data-testid="save-draft-btn"
              >
                {loading ? 'Saving...' : savedId ? 'Update Draft' : 'Save Draft'}
              </button>
              <button
                onClick={handleOpenInvestigation}
                className="ff-btn-outline"
                data-testid="open-investigation-btn"
              >
                Open in Investigation Mode
              </button>
              <button
                onClick={handleAnalyze}
                className="ff-btn-primary"
                disabled={loading || caseData.narrative.length < 20}
                data-testid="generate-record-btn"
              >
                {loading ? 'Generating...' : 'Generate Record'}
              </button>
            </div>
          </div>
          <div className="ff-im-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <img src="/stewie_front.png" alt="Stewie" data-testid="stewie-logo" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center 25%', border: '2px solid #334155' }} />
              <div className="ff-badge ff-badge-record">Full Record</div>
            </div>
            <h1>Build a Complete Case Record</h1>
            <p>Turn rough notes into a full summary, timeline, and documentation packet.</p>
          </div>
        </div>

        {/* Status Messages */}
        {savedId && (
          <div className="ff-success-message" data-testid="success-message">
            {loadedCase ? 'Draft updated.' : 'Draft saved.'} Case ID: {savedId}
          </div>
        )}
        {error && (
          <div className="ff-error-message" data-testid="error-message">{error}</div>
        )}

        {/* Tab Navigation */}
        {analysis && (
          <div className="ff-tabs">
            <button
              className={`ff-tab ${activeTab === 'input' ? 'active' : ''}`}
              onClick={() => setActiveTab('input')}
            >
              Edit Information
            </button>
            <button
              className={`ff-tab ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => setActiveTab('results')}
            >
              View Record
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="ff-im-content">
          <div className="ff-im-main">
            {activeTab === 'input' ? (
              <>
                <BasicInfoForm
                  caseData={caseData}
                  setCaseData={setCaseData}
                  investigationMode={false}
                />
                <NarrativeBuilder
                  caseData={caseData}
                  setCaseData={setCaseData}
                />
                <WitnessCapture
                  witnesses={caseData.witnesses}
                  setWitnesses={setWitnesses}
                />
                <EvidenceUpload
                  evidence={caseData.evidence}
                  setEvidence={setEvidence}
                />
                {analysis?.follow_up_questions?.length > 0 && (
                  <FollowUpQuestions
                    questions={analysis.follow_up_questions}
                    answers={caseData.follow_up_answers}
                    setAnswers={handleFollowUpChange}
                  />
                )}
              </>
            ) : (
              <>
                {analysis?.timeline?.length > 0 && (
                  <TimelineView timeline={analysis.timeline} />
                )}
                <OutputsPanel
                  analysis={analysis}
                  caseData={caseData}
                  showPacketSummary={true}
                  onExportTracked={() => analyticsTracker.trackFactFinderExportedPdf('full_record')}
                />
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="ff-im-sidebar">
            <MissingInfoChecklist items={missingItems} />
            <RiskFlagsPanel flags={analysis?.risk_flags || []} />
            <div className="ff-reminder-card">
              <h3>Documentation Tips</h3>
              <ul>
                <li>Include as many facts as possible.</li>
                <li>Add witnesses who can support your account.</li>
                <li>Attach any evidence (photos, emails, messages).</li>
                <li>Review the generated record before sharing.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FullRecordPage;
